import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';

import { ROUTES } from '@/constants';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ExecutiveAppHeader } from '@/components/layout/ExecutiveAppHeader';
import { useOperationalDashboardAccess } from '@/features/operationalDashboard/context/useOperationalDashboardAccess';
import { DashboardHeaderSlotProvider, DashboardHeaderSlot } from '@/context/DashboardHeaderSlotContext';
import { EscalationHistoryProvider } from '@/features/escalation/context/EscalationHistoryContext';
import { mockTaskForces } from '@/features/leadership/data/mockLeadershipData';
import { ChatProvider } from '@/features/chat/context/ChatContext';
import { AiAssistantProvider } from '@/features/aiAssistant/context/AiAssistantContext';

import { ExecFilterContext } from './ExecFilterContext';

export function ExecutiveLayout() {
    const { hasAccess: hasOperationalDashboard } = useOperationalDashboardAccess();
    const isMobile = useIsMobile();
    const { tfId, pillarId } = useParams();
    const { pathname } = useLocation();

    const [selectedTF, setSelectedTF] = useState('all');
    const [selectedCity, setSelectedCity] = useState('all');
    const [selectedTime, setSelectedTime] = useState('monthly');

    const filterCtx = useMemo(
        () => ({
            selectedTF,
            setSelectedTF,
            selectedCity,
            setSelectedCity,
            selectedTime,
            setSelectedTime,
        }),
        [selectedTF, selectedCity, selectedTime],
    );

    const { headerTitle, headerSubtitle } = useMemo(() => {
        const tf = tfId ? mockTaskForces.find((x) => x.id === tfId) : null;
        const pillar = tf && pillarId ? (tf.pillars || []).find((p) => p.id === pillarId) : null;
        if (pillar && tf) {
            return {
                headerTitle: pillar.name,
                headerSubtitle: `${tf.name} · Pillar overview`,
            };
        }
        if (tf) {
            return {
                headerTitle: tf.name,
                headerSubtitle: 'Task force overview · Executive view',
            };
        }
        return {
            headerTitle: 'Executive Dashboard',
            headerSubtitle: 'Readiness Platform',
        };
    }, [tfId, pillarId]);

    const showPortfolioFilters = !tfId;

    /**
     * Export is the one executive route that is a document rather than a
     * dashboard.
     *
     * Every other page in this shell is built to fit the viewport exactly and
     * scroll its own panes, which is why the shell clamps `main` to
     * `overflow-hidden`. The export page is a long form above a report preview
     * of unbounded length, so under that clamp its lower half is simply
     * unreachable. It gets the scrollbar back; nothing else in the shell
     * changes.
     */
    const isExportRoute = pathname.startsWith(ROUTES.EXECUTIVE_EXPORT);

    return (
        <ExecFilterContext.Provider value={filterCtx}>
            <DashboardHeaderSlotProvider>
                <ChatProvider>
                    <AiAssistantProvider>
                    <EscalationHistoryProvider>
                        <div className="flex h-screen h-dvh flex-col gap-3 overflow-hidden bg-background p-3">
                            <ExecutiveAppHeader
                                title={headerTitle}
                                subtitle={headerSubtitle}
                                center={<DashboardHeaderSlot />}
                                showCountdown
                                structureHref={ROUTES.EXECUTIVE_STRUCTURE}
                            operationalDashboardHref={
                                hasOperationalDashboard
                                    ? ROUTES.EXECUTIVE_OPERATIONAL_DASHBOARD
                                    : null
                            }
                                exportHref={ROUTES.EXECUTIVE_EXPORT}
                                manualHelpHref={ROUTES.EXECUTIVE_HELP}
                            />

                            <main
                                className={cn(
                                    'min-h-0 flex-1 overflow-x-hidden',
                                    showPortfolioFilters && !isMobile && !isExportRoute
                                        ? 'overflow-hidden'
                                        : 'overflow-y-auto',
                                )}
                            >
                                <Outlet />
                            </main>
                        </div>
                    </EscalationHistoryProvider>
                    </AiAssistantProvider>
                </ChatProvider>
            </DashboardHeaderSlotProvider>
        </ExecFilterContext.Provider>
    );
}

export default ExecutiveLayout;
